import { Link } from "react-router-dom";
import { matches } from "@/mocks/matches";
import { teams } from "@/mocks/teams";

export default function HeroLive() {
  const live = matches.find((m) => m.status === "live") ?? matches[0];
  const home = teams.find((t) => t.id === live.homeTeamId);
  const away = teams.find((t) => t.id === live.awayTeamId);

  const possession = live.stats?.possession ?? [50, 50];

  return (
    <section className="relative w-full overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={live.cover}
          alt={`${home?.name} 对阵 ${away?.name} 欧冠直播`}
          title={`${home?.name} vs ${away?.name} 欧冠直播`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-950/70 via-primary-950/60 to-primary-950/90"></div>
      </div>

      <div className="relative w-full px-4 md:px-6 pt-28 md:pt-36 pb-14 md:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-7 text-background-50">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold tracking-wide">
              <span className="w-2 h-2 rounded-full bg-background-50 animate-pulse-live"></span>
              正在直播 · 第 {live.minute} 分钟
            </span>

            <h1 className="mt-5 font-heading text-3xl md:text-5xl lg:text-6xl font-bold leading-tight">
              {home?.name} <span className="text-accent-400">对阵</span> {away?.name}
            </h1>

            <p className="mt-4 max-w-2xl text-sm md:text-base leading-relaxed text-background-200">
              {live.preview}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-background-100">
              <span className="inline-flex items-center gap-2">
                <i className="ri-calendar-line text-accent-400" aria-hidden="true"></i>
                {live.date} {live.time}
              </span>
              <span className="inline-flex items-center gap-2">
                <i className="ri-map-pin-line text-accent-400" aria-hidden="true"></i>
                {live.venue}
              </span>
              <span className="inline-flex items-center gap-2">
                <i className="ri-flag-line text-accent-400" aria-hidden="true"></i>
                {live.stage} · {live.round}
              </span>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to={`/live/${live.id}`}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-accent-500 px-6 py-3 text-base font-semibold text-background-50 hover:bg-accent-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-live-line text-lg" aria-hidden="true"></i>
                进入直播
              </Link>
              <Link
                to={`/detail/${live.id}`}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-background-200/60 bg-white/10 px-6 py-3 text-base font-semibold text-background-50 hover:bg-white/20 transition-colors whitespace-nowrap"
              >
                <i className="ri-file-list-3-line text-lg" aria-hidden="true"></i>
                比赛详情
              </Link>
              <Link
                to="/schedule/2025-26"
                className="inline-flex items-center justify-center gap-2 rounded-md px-6 py-3 text-base font-semibold text-background-100 hover:text-background-50 hover:bg-white/10 transition-colors whitespace-nowrap"
              >
                完整赛程
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-lg border border-white/15 bg-white/10 backdrop-blur-md p-5 md:p-6">
              <div className="flex items-center justify-between mb-5">
                <span className="text-xs font-semibold tracking-[0.2em] uppercase text-background-200">
                  实时比分
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-400 animate-pulse-live"></span>
                  LIVE
                </span>
              </div>

              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 flex flex-col items-center gap-2">
                  <span className="w-14 h-14 rounded-full bg-background-50 flex items-center justify-center p-2 overflow-hidden">
                    <img
                      src={home?.crest}
                      alt={`${home?.name}队徽`}
                      title={`${home?.name} 欧冠直播`}
                      className="w-full h-full object-contain"
                    />
                  </span>
                  <span className="text-sm font-medium text-background-50 text-center">
                    {home?.name}
                  </span>
                </div>

                <div className="text-center px-2">
                  <div className="font-heading text-4xl md:text-5xl font-bold text-background-50">
                    {live.homeScore}
                    <span className="mx-1 text-background-300">:</span>
                    {live.awayScore}
                  </div>
                  <span className="mt-1 inline-block text-xs text-accent-400 font-semibold">
                    第 {live.minute} 分钟
                  </span>
                </div>

                <div className="flex-1 flex flex-col items-center gap-2">
                  <span className="w-14 h-14 rounded-full bg-background-50 flex items-center justify-center p-2 overflow-hidden">
                    <img
                      src={away?.crest}
                      alt={`${away?.name}队徽`}
                      title={`${away?.name} 欧冠直播`}
                      className="w-full h-full object-contain"
                    />
                  </span>
                  <span className="text-sm font-medium text-background-50 text-center">
                    {away?.name}
                  </span>
                </div>
              </div>

              <div className="mt-6">
                <div className="flex items-center justify-between text-xs text-background-200 mb-2">
                  <span>控球率 {possession[0]}%</span>
                  <span>{possession[1]}%</span>
                </div>
                <div className="h-2 w-full rounded-full bg-white/20 overflow-hidden flex">
                  <div className="h-full bg-accent-500" style={{ width: `${possession[0]}%` }}></div>
                  <div className="h-full bg-secondary-400" style={{ width: `${possession[1]}%` }}></div>
                </div>
              </div>

              <Link
                to={`/live/${live.id}`}
                className="mt-6 flex items-center justify-center gap-2 rounded-md bg-background-50 px-4 py-2.5 text-sm font-semibold text-primary-950 hover:bg-background-100 transition-colors"
              >
                <i className="ri-play-circle-line text-lg" aria-hidden="true"></i>
                观看实时画面
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}